import type { CSSProperties, KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { useRef, useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import css from './tag-input.module.scss';

const styles = pickCss(css, css.root);

type Size = 'sm' | 'default' | 'lg';

type TagInputProps = {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: Size;
  className?: string;
  style?: CSSProperties;
  id?: string;
  // Maximum tag length — guards against malformed paste / accidental
  // long strings. Tags exceeding the limit are silently truncated on commit.
  maxLength?: number;
};

const normalise = (raw: string, maxLength?: number): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return maxLength ? trimmed.slice(0, maxLength) : trimmed;
};

export const TagInput = ({
  value,
  onChange,
  placeholder = 'Add a tag…',
  disabled,
  size = 'default',
  className,
  style,
  id,
  maxLength,
}: TagInputProps) => {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commitDraft = (raw: string): boolean => {
    const next = normalise(raw, maxLength);
    if (!next) return false;
    if (value.includes(next)) return false;
    onChange([...value, next]);
    return true;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (commitDraft(draft)) setDraft('');
      return;
    }
    if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (commitDraft(draft)) setDraft('');
  };

  const handleRemove = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
    inputRef.current?.focus();
  };

  return (
    <div
      className={styles({ size, isDisabled: !!disabled }, className)}
      style={style}
    >
      <ul aria-label="Selected tags" className={css.list}>
        {value.map((tag) => (
          <li className={css.tag} key={tag}>
            <span className={css.tagLabel}>{tag}</span>
            <button
              aria-label={`Remove tag ${tag}`}
              className={css.tagRemove}
              disabled={disabled}
              onClick={() => handleRemove(tag)}
              type="button"
            >
              <X size={12} />
            </button>
          </li>
        ))}
        <li className={css.inputItem}>
          <input
            aria-label="Add tag"
            className={css.input}
            disabled={disabled}
            id={id}
            onBlur={handleBlur}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ''}
            ref={inputRef}
            type="text"
            value={draft}
          />
        </li>
      </ul>
    </div>
  );
};

export type { TagInputProps };
